import ActionText from '../core/ActionText';
import Errored from './Errored';
import Tooltip from '@material-ui/core/Tooltip';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import { makeCancelable } from './utils/utils';
import Avatar from '@material-ui/core/Avatar';
import Chip from '@material-ui/core/Chip';
import { withStyles } from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import GS from '../grafsync/grafsync';
import { globalStyles } from './style/gstheme';

const styles = theme => {
    return Object.assign({
        link: {
            color: 'white',
            marginRight: 10,
            marginBottom: 10,
            maxWidth: '100%',
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
                cursor: 'pointer'
            }
        },
        linkedThumbnail: {
            maxWidth: 250,
            maxHeight: 220,
            float: 'left',
            marginTop: 12,
            marginRight: 12,
            marginBottom: 12
        },
        abstract: {
            clear: 'both',
            padding: '0px 12px 12px 0px',
            overflow: 'scroll',
            height: 270
        },
        popoverRoot: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        }
    }, globalStyles);
};

class ExternalResourceLink extends React.Component {

    handleDeleteLink() {
        // We do this directly to ensure direction of link delete is correct 
        GS.deleteProperty(this.props.source, this.props.graph, this.props.property, this.props.id, null, null, this.props.propertyGraph);
    }

    constructor(props) {
        super(props);
        this.state = {
            showInfoDetails: false,
            isErrored: null,
            anchorEl: null,
            hideImage: false
        };
        this.cancellable = null;
    }

    showInfo = async (event) => {
        this.setState({ anchorEl: event.currentTarget }, async () => {
            try {
                const id = this.props.id;
                const query = `SELECT * {
<${id}> rdfs:label ?label .
FILTER (LANG(?label) = "en")
OPTIONAL {
<${id}> <http://dbpedia.org/ontology/thumbnail> ?thumbnail .
?thumbnail <http://purl.org/dc/elements/1.1/rights> ?rights .
}
OPTIONAL {
<${id}> <http://dbpedia.org/ontology/abstract> ?abstract .
FILTER (LANG(?abstract) = "en")
}
OPTIONAL {
<${id}> dbo:wikiPageRedirects ?redirect .
?redirect rdfs:label ?redirectLabel .
FILTER (LANG(?redirectLabel) = "en")
?redirect <http://dbpedia.org/ontology/abstract> ?redirectAbstract. 
FILTER (LANG(?redirectAbstract) = "en")
?redirect <http://dbpedia.org/ontology/thumbnail> ?redirectThumbnail . 
?redirectThumbnail <http://purl.org/dc/elements/1.1/rights> ?redirectRights .
}
}`;
                const url = `https://dbpedia.org/sparql?default-graph-uri=http://dbpedia.org&query=${encodeURIComponent(query)}&format=application/json`;
                const responseData = await this.loadData(url).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                const result = responseData.results && responseData.results.bindings && responseData.results.bindings.length > 0 ?
                    responseData.results.bindings[0]
                    :
                    {};
                const thumbnail = result.thumbnail ? result.thumbnail.value
                    :
                    result.redirectThumbnail ?
                        result.redirectThumbnail.value
                        :
                        "";
                const rights = result.rights ?
                    result.rights.value
                    :
                    result.redirectRights ?
                        result.redirectRights.value
                        :
                        "";
                const credit = rights.includes("commons.wikimedia.org") ?
                    "Wikimedia"
                    :
                    rights.includes("en.wikipedia.org") ?
                        "English Wikipedia"
                        :
                        thumbnail;
                const data = result.label ?
                    {
                        thumbnail,
                        credit,
                        rights,
                        redirect: result.redirect ? result.redirect.value : null,
                        abstract: result.abstract ? result.abstract.value : null,
                        redirectAbstract: result.redirectAbstract ? result.redirectAbstract.value : null,
                        name: result.label.value,
                        redirectName: result.redirectLabel ? result.redirectLabel.value : null
                    }
                    :
                    {
                        error: "Not available"
                    };
                this.setState({ showInfoDetails: true, data });
            }
            catch (e) {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) {
                    this.setState({ isErrored: e });
                }
            }
        });
    };

    loadData(id) {
        this.cancellable = makeCancelable(GS.doGetRequest(id));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    hideImage = () => this.setState({ hideImage: true });

    hideInfoDetails = () => this.setState({ showInfoDetails: false });

    clearError = () => this.setState({ isErrored: false });

    render() {

        const { classes, isReadOnly, id, dataTest, source, invalid } = this.props;
        const { showInfoDetails, anchorEl, data, isErrored, hideImage } = this.state;
        const isLinkedData = id.includes("dbpedia.org");

        if (isErrored) return <Errored dataTest="el-error" data={isErrored} clearError={this.clearError} />;

        return (
            <React.Fragment>
                <Chip
                    data-test={dataTest}
                    color="primary"
                    className={classes.link}
                    style={{
                        ...(invalid) && { backgroundColor: '#FF6F00' }
                    }}
                    avatar={
                        <Tooltip title="Visit this URL">
                            <Avatar style={{ backgroundColor: 'inherit' }}>
                                <a href={id} target="_blank" rel="noreferrer">
                                    <OpenInNewIcon fontSize="small" />
                                </a>
                            </Avatar>
                        </Tooltip>}
                    onDelete={!isReadOnly && source ?
                        ((ev) => {
                            this.handleDeleteLink();
                            ev.preventDefault();
                            ev.stopPropagation();
                        })
                        :
                        null
                    }
                    label={<span onClick={isLinkedData ? this.showInfo : null}>
                        {isLinkedData ?
                            <Tooltip title="Click to view linked data (in English)"><span>{id}</span></Tooltip>
                            :
                            <a href={id} target="_new" data-test={invalid ? "invalid-link" : "valid-link"}>{id}</a>
                        }</span>} />
                {showInfoDetails && anchorEl &&
                    <Popover
                        id={id}
                        data-test="infobox"
                        open={true}
                        anchorReference={'none'}
                        onClose={this.hideInfoDetails}
                        classes={{
                            root: classes.popoverRoot,
                        }}
                        anchorOrigin={{
                            vertical: 'center',
                            horizontal: 'left',
                        }}
                    >
                        <div style={{ padding: '24px 24px 12px 24px', width: 500, overflow: 'hidden' }}>
                            <div style={{ backgroundColor: '#13294B', color: 'white', padding: 12 }}>
                                <Typography
                                    variant="body2"
                                    style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}><a href={id} target="_new">{id}</a></Typography>
                                {data.redirect && <Typography
                                    variant="caption"
                                    data-test="redirect-url"
                                    style={{ color: 'white', overflow: 'hidden', textOverflow: 'ellipsis' }}>Redirected to <a href={data.redirect} target="_new">{data.redirect}</a></Typography>}
                            </div>
                            {data.error ?
                                <div style={{ height: 200 }}>
                                    <ActionText offset="120px">We cannot retrieve the details for this resource at this time.</ActionText>
                                </div>
                                :
                                <div>
                                    <div style={{ marginBottom: 12 }}>
                                        {(data.thumbnail) &&
                                            <div data-test="thumbnail" className={classes.linkedThumbnail}>
                                                {hideImage ?
                                                    <Typography paragraph variant="body2">Image not available.</Typography>
                                                    :
                                                    <img alt="" onError={this.hideImage} style={{ maxWidth: 250, maxHeight: 200 }} src={data.thumbnail} />
                                                }
                                                <Typography variant="caption">Photo Credit: <a href={data.rights} target="_new">{data.credit}</a></Typography>
                                            </div>
                                        }
                                        <Typography data-test="dbpedia-title" paragraph className={classes.mainHeading} style={{ paddingTop: 12 }}>{data.name}</Typography>
                                        {data.redirectName && <div>
                                            <Typography variant="caption">Redirected To</Typography>
                                            <Typography data-test="redirect-title" paragraph className={classes.mainHeading}>{data.redirectName}</Typography>
                                        </div>}
                                    </div>
                                    <div data-test="abstract" tabIndex={0} className={classes.abstract}>
                                        <Typography variant="body2">{data.abstract || data.redirectAbstract}</Typography>
                                    </div>
                                    <div style={{ marginTop: 12, borderTop: 'solid 1px #e0e0e0', paddingTop: 6 }}>
                                        <Typography variant="caption">This data is from <a href="https://dbpedia.org">DBpedia</a></Typography>
                                    </div>
                                </div>
                            }
                        </div>
                    </Popover>
                }
            </React.Fragment>
        );
    }
}

ExternalResourceLink.defaultProps = {
    isReadOnly: false,
    invalid: false
};

ExternalResourceLink.propTypes = {
    dataTest: PropTypes.string,
    id: PropTypes.string.isRequired,
    graph: PropTypes.string,
    propertyGraph: PropTypes.string,
    invalid: PropTypes.bool,
    source: PropTypes.string,
    isReadOnly: PropTypes.bool,
    property: PropTypes.string,
    classes: PropTypes.object
};

export default withRouter(withStyles(styles)(ExternalResourceLink));